
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../apiClient';
import { useAuth } from '../firebase';

const componentTranslations = {
    ja: {
        title: "メール受信設定",
        importantNotice: "重要なお知らせ",
        importantNoticeDesc: "サービス利用上重要な案内やセキュリティ関連通知",
        maintenanceNotice: "障害・メンテナンス情報",
        maintenanceNoticeDesc: "障害発生情報、復旧情報、計画メンテナンスのお知らせ",
        signalNews: "シグナルからのお知らせ",
        signalNewsDesc: "新機能リリース、サービス改善、キャンペーンなどのお知らせ",
        saveSuccess: "設定を更新しました。",
        saveError: "設定の更新に失敗しました。",
        loading: "読み込み中...",
    },
    en: {
        title: "Email Notification Settings",
        importantNotice: "Important Notices",
        importantNoticeDesc: "Important service updates and security notifications",
        maintenanceNotice: "Maintenance and Outages",
        maintenanceNoticeDesc: "Information about outages, recoveries, and planned maintenance",
        signalNews: "Signal News",
        signalNewsDesc: "Announcements about new features, improvements, and campaigns",
        saveSuccess: "Settings updated successfully.",
        saveError: "Failed to update settings.",
        loading: "Loading...",
    }
};

const ToggleSwitchStyles = () => (
    <style>{`
        .toggle-switch {
            position: relative;
            display: inline-block;
            width: 50px;
            height: 28px;
        }
        .toggle-switch input { display: none; }
        .slider {
            position: absolute;
            cursor: pointer;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: #ccc;
            transition: .4s;
            border-radius: 28px;
        }
        .slider:before {
            position: absolute;
            content: "";
            height: 20px;
            width: 20px;
            left: 4px;
            bottom: 4px;
            background-color: white;
            transition: .4s;
            border-radius: 50%;
        }
        input:checked + .slider {
            background-color: #4F46E5; /* Indigo 600 */
        }
        input:checked + .slider:before {
            transform: translateX(22px);
        }
        input:disabled + .slider {
            cursor: not-allowed;
            background-color: #e0e0e0;
        }
    `}</style>
);

const ToggleItem = ({ id, label, description, checked, onChange, disabled }) => (
    <div className="flex items-center justify-between py-4 border-b border-gray-100 last:border-0">
        <div className="flex-1 pr-4">
            <h3 className={`text-base font-semibold ${disabled ? 'text-gray-400' : 'text-gray-800'}`}>
                {label}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
                {description}
            </p>
        </div>
        <label
            htmlFor={id}
            className="toggle-switch flex-shrink-0"
            style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
            onClick={(e) => {
                e.preventDefault();
                if (!disabled) {
                    onChange();
                }
            }}
        >
            <input id={id} type="checkbox" checked={checked} readOnly disabled={disabled} />
            <span className="slider round"></span>
        </label>
    </div>
);

const NotificationSettings = () => {
    const { t, i18n } = useTranslation();
    const currentUser = useAuth();
    const [settings, setSettings] = useState({
        importantNotice: true,
        maintenanceNotice: true,
        signalNews: true
    });
    const [loading, setLoading] = useState(true);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        Object.entries(componentTranslations).forEach(([lang, resources]) => {
            i18n.addResourceBundle(lang, 'translation', resources, true, true);
        });
    }, [i18n]);

    useEffect(() => {
        const fetchSettings = async () => {
            if (!currentUser) return;
            try {
                const token = await currentUser.getIdToken();
                const response = await apiClient.get('/v1/user/notification-settings', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    setSettings({
                        importantNotice: true, // Always true
                        maintenanceNotice: data.maintenanceNotice !== undefined ? data.maintenanceNotice : true,
                        signalNews: data.signalNews !== undefined ? data.signalNews : true,
                    });
                }
            } catch (err) {
                console.error("Error fetching notification settings:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, [currentUser]);

    const handleToggle = async (key) => {
        if (key === 'importantNotice') return;

        const newSettings = { ...settings, [key]: !settings[key] };
        setSettings(newSettings);
        setError('');
        setSuccess('');

        try {
            const token = await currentUser.getIdToken();
            const response = await apiClient.post('/v1/user/notification-settings', newSettings, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                setSuccess(t('saveSuccess'));
                setTimeout(() => setSuccess(''), 3000);
            } else {
                throw new Error('Failed to update');
            }
        } catch (err) {
            setError(t('saveError'));
            setSettings(settings); // Revert
            console.error(err);
        }
    };

    if (loading) {
        return <div className="p-8 text-center">{t('loading')}</div>;
    }

    return (
        <>
            <ToggleSwitchStyles />
            <div className="bg-white p-8 w-full max-w-4xl mx-auto my-8 shadow-lg rounded-lg">
                <div className="flex justify-between items-center mb-6 pb-2 border-b-2 border-gray-200">
                    <h2 className="text-2xl font-bold text-gray-900">{t('title')}</h2>
                </div>

                {success && (
                    <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-lg flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        {success}
                    </div>
                )}

                {error && (
                    <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {error}
                    </div>
                )}

                <div className="space-y-2">
                    <ToggleItem
                        id="importantNotice"
                        label={t('importantNotice')}
                        description={t('importantNoticeDesc')}
                        checked={settings.importantNotice}
                        onChange={() => {}}
                        disabled={true}
                    />
                    <ToggleItem
                        id="maintenanceNotice"
                        label={t('maintenanceNotice')}
                        description={t('maintenanceNoticeDesc')}
                        checked={settings.maintenanceNotice}
                        onChange={() => handleToggle('maintenanceNotice')}
                        disabled={false}
                    />
                    <ToggleItem
                        id="signalNews"
                        label={t('signalNews')}
                        description={t('signalNewsDesc')}
                        checked={settings.signalNews}
                        onChange={() => handleToggle('signalNews')}
                        disabled={false}
                    />
                </div>
            </div>
        </>
    );
};

export default NotificationSettings;
